import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X } from 'lucide-react';
import SupaHelpFlow from './SupaHelpFlow';

// Standalone campaign landing pages render no site chrome (no nav, no chat widget).
const STANDALONE_ROUTES = ['/heat-pump-upgrade'];

const SupaHelpWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll on mobile when widget is open
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      }
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  if (STANDALONE_ROUTES.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2 bg-[#3D5A47] text-white px-4 py-3 rounded-full shadow-lg hover:bg-[#2d4435] transition-all duration-300 group ${
          isOpen ? 'opacity-0 pointer-events-none scale-90' : 'opacity-100 scale-100'
        }`}
        aria-label="Open Supa Help"
        data-testid="supahelp-widget-button"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="font-medium">Supa Help</span>
        <span className="hidden sm:block text-xs text-white/70 border-l border-white/30 pl-2 ml-1">
          Advice • Quotes • Support
        </span>
      </button>

      {/* Widget Panel - Desktop */}
      {!isMobile && (
        <div
          className={`fixed bottom-6 right-6 z-[60] w-[400px] max-h-[600px] bg-white rounded-2xl shadow-2xl transition-all duration-300 overflow-hidden ${
            isOpen
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
          }`}
          data-testid="supahelp-widget-panel"
        >
          <div className="p-5 max-h-[600px] overflow-y-auto">
            <SupaHelpFlow source="widget" onClose={() => setIsOpen(false)} />
          </div>
        </div>
      )}

      {/* Widget Panel - Mobile (Full Screen) */}
      {isMobile && (
        <div
          className={`fixed inset-0 z-[60] bg-white transition-all duration-300 ${
            isOpen
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-full pointer-events-none'
          }`}
          data-testid="supahelp-widget-mobile"
        >
          <div className="h-full overflow-y-auto p-4 pt-6">
            <SupaHelpFlow source="widget" onClose={() => setIsOpen(false)} />
          </div>
        </div>
      )}

      {/* Backdrop for desktop */}
      {!isMobile && isOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/20 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default SupaHelpWidget;
